import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface Options {
  margin: number;
  startY: number;
  scale: number;
  autoPaginate: boolean;
}

// TODO: This function is a bit messy, but hopefully temporary.
// Looking to replace with a PDF solution on the backend.

export async function generatePdf(
  element: HTMLElement,
  filePath: string,
  fontSizeScaler: number = 1,
): Promise<void> {
  const doc = new jsPDF('p', 'pt', 'a4');

  const options: Options = {
    margin: 30,
    startY: 30,
    scale: 0.5,
    autoPaginate: true,
  };

  const canvasHeight = doc.internal.pageSize.getHeight() - 2 * options.margin;
  let currentY = options.startY;
  const paddingBetweenElements = 10;

  // Define alert background colors
  const alertColors: Record<string, [number, number, number]> = {
    'alert-warning': [255, 223, 186], // Light orange
    'alert-info': [173, 216, 230], // Light blue
    'alert-success': [144, 238, 144], // Light green
    'alert-danger': [240, 128, 128], // Light red
  };

  const addTextToPage = async (text: HTMLElement): Promise<void> => {
    const textStyles = window.getComputedStyle(text);
    const fontSize = parseFloat(textStyles.fontSize) * fontSizeScaler;
    doc.setFontSize(fontSize);
    doc.setTextColor(0, 0, 0);
    const textContent = text.textContent?.trim() || '';
    const textHeight = doc.getTextDimensions(textContent).h;

    if (currentY + textHeight > canvasHeight) {
      doc.addPage();
      currentY = options.startY;
    }

    const splitText = doc.splitTextToSize(
      textContent,
      doc.internal.pageSize.getWidth() - 2 * options.margin,
    );
    splitText.forEach((txt: string) => doc.text(txt, options.margin, (currentY += textHeight)));
    currentY += paddingBetweenElements;
  };

  const addPadding = () => {
    currentY += paddingBetweenElements;
  };

  const processList = async (list: HTMLElement) => {
    const listItems = list.getElementsByTagName('li');
    const isOrderedList = list.tagName === 'OL';
    let itemIndex = 1;
    const listMargin = options.margin + 10;

    for (const item of listItems) {
      const textStyles = window.getComputedStyle(item);
      const fontSize = parseFloat(textStyles.fontSize) * fontSizeScaler;
      doc.setFontSize(fontSize);
      doc.setTextColor(0, 0, 0);
      const textContent = item.textContent?.trim() || '';
      const textHeight = doc.getTextDimensions(textContent).h;

      if (currentY + textHeight > canvasHeight) {
        doc.addPage();
        currentY = options.startY;
      }

      const prefix = isOrderedList ? `${itemIndex}. ` : '• ';
      const splitText = doc.splitTextToSize(
        prefix + textContent,
        doc.internal.pageSize.getWidth() - 2 * listMargin,
      );

      splitText.forEach((txt: string, index: number) => {
        const xPosition = index === 0 ? listMargin : listMargin + 12;
        doc.text(txt, xPosition, (currentY += textHeight));
      });

      currentY += paddingBetweenElements;
      itemIndex++;
    }
    currentY += paddingBetweenElements;
  };

  const processCanvasElement = async (element: HTMLElement, scale: number = 1) => {
    try {
      const canvas = await html2canvas(element, {scale, useCORS: true});
      const elementWidth = canvas.width / 2;
      const elementHeight = canvas.height / 2;
      const pageWidth = doc.internal.pageSize.getWidth();
      const xPosition = (pageWidth - elementWidth) / 2;

      if (currentY + elementHeight > canvasHeight) {
        doc.addPage();
        currentY = options.startY;
      }
      doc.addImage(canvas, 'PNG', xPosition, currentY, elementWidth, elementHeight);
      currentY += elementHeight + 30;
    } catch (error) {
      console.error('Error processing element:', error);
    }
  };

  const measureAlertBoxHeight = async (alert: HTMLElement): Promise<number> => {
    let totalHeight = 0;
    const padding = 10;
    const elementPaddingBetween = 5; // Adjust padding between elements

    // Add top padding
    totalHeight += padding;

    // Measure each child node inside the alert box
    for (const node of alert.childNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const textStyles = window.getComputedStyle(element);
        const fontSize = parseFloat(textStyles.fontSize) * fontSizeScaler;
        doc.setFontSize(fontSize);
        const textContent = element.textContent?.trim() || '';
        const splitText = doc.splitTextToSize(
          textContent,
          doc.internal.pageSize.getWidth() - 2 * options.margin - 2 * padding,
        );
        const textHeight = splitText.length * doc.getTextDimensions(textContent).h;
        totalHeight += textHeight + elementPaddingBetween;
      }
    }

    // Add bottom padding
    totalHeight += padding - elementPaddingBetween;

    return totalHeight;
  };

  const processAlertBox = async (alert: HTMLElement) => {
    const alertClass = [...alert.classList].find((cls) =>
      Object.prototype.hasOwnProperty.call(alertColors, cls),
    );
    const bgColor: [number, number, number] = alertClass
      ? alertColors[alertClass]
      : [255, 255, 255];

    const padding = 10;
    const maxWidth = doc.internal.pageSize.getWidth() - 2 * options.margin;
    const cornerRadius = 8;

    let startY = currentY;

    // Measure the height of the alert box and its content
    const alertHeight = await measureAlertBoxHeight(alert);

    // Check if the alert box fits on the remaining page space
    if (startY + alertHeight > canvasHeight) {
      doc.addPage();
      startY = options.startY;
      currentY = startY; // Reset currentY to startY after adding a new page
    }

    // Draw the background box first
    doc.setFillColor(...bgColor);
    doc.roundedRect(options.margin, startY, maxWidth, alertHeight, cornerRadius, cornerRadius, 'F');

    // Add top padding before processing child nodes
    currentY += padding;

    // Add horizontal padding before processing child nodes
    const originalMargin = options.margin;
    options.margin += padding;

    // Process each child node inside the alert box
    for (const node of alert.childNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        await processElement(node as HTMLElement);
      }
    }

    // Restore original margin
    options.margin = originalMargin;

    const totalHeight = currentY - startY + padding - paddingBetweenElements; // Adjust to remove extra space

    currentY = startY + totalHeight + paddingBetweenElements;
  };

  const processImage = async (image: HTMLImageElement) => {
    await addPadding();
    await processCanvasElement(image);
  };

  const processTable = async (table: HTMLElement) => {
    await addPadding();
    await processCanvasElement(table);
  };

  const processElement = async (node: Node): Promise<void> => {
    if (!(node instanceof HTMLElement)) return;
    if (node.classList.contains('alert')) {
      await processAlertBox(node);
    } else if (node.tagName === 'P' || /^H[1-6]$/.test(node.tagName) || node.tagName === 'SUP') {
      await addTextToPage(node);
    } else if (node.tagName === 'UL' || node.tagName === 'OL') {
      await processList(node);
    } else if (node.tagName === 'IMG') {
      await processImage(node as HTMLImageElement);
    } else if (node.tagName === 'TABLE') {
      await processTable(node);
    } else {
      for (const child of Array.from(node.childNodes)) {
        await processElement(child);
      }
    }
  };

  await processElement(element);
  doc.save(filePath);
}
